import extractSearchString from '../../../../../inz/src/js/utils/extractSearchString'
import MoreInfo from '../../../../../inz/src/js/components/MoreInfo'
import ReactDOM from 'react-dom'

const AVAILABILITY_API_URL = document.getElementById('main')?.getAttribute('data-search-domain')
const form = document.getElementById('domain-lookup-form')
const input = form?.querySelector('input')
const submit = form?.querySelector('.c-domain-lookup__search')
const clear = form?.querySelector('.c-domain-lookup__clear')
const tick = document.querySelector('.c-domain-lookup__tick')?.cloneNode(true)
tick?.classList.remove('c-domain-lookup__tick--hide')
const cross = document.querySelector('.c-domain-lookup__cross')?.cloneNode(true)
cross?.classList.remove('c-domain-lookup__cross--hide')
const TLDs = (form?.getAttribute('data-tlds') || '')
  .split(',')
  .map((tld) => tld.trim())
  .filter((tld) => !!tld)
const ALLOWED_CHARACTERS_REGEX = /[^a-zA-ZāēīōūĀĒĪŌŪ0-9-]/g
const unsupportedMessage = form?.querySelector('.c-domain-lookup__unsupported-message')
let resultsPanelOpen = false
let hasUnsupportedCharacters = false

export default () => {
  if (!input || !submit || !AVAILABILITY_API_URL || !TLDs.length) {
    return
  }

  // REGISTER HANDLERS
  form.addEventListener('submit', (e) => {
    e.preventDefault()
    if (input.value.length) {
      input.value = input.value.toLowerCase()
      const searchString = extractSearchString(input.value, true)
      const cleanedSearchString = removeUnsupportedCharacters(searchString)
      if (cleanedSearchString) {
        doLookup(cleanedSearchString)
      }
    }
  })
  input.addEventListener('keyup', () => {
    toggleClearButton(input.value.length > 0)
  })
  clear.addEventListener('click', () => {
    input.value = ''
    toggleClearButton(false, true)
  })

  const errorMsg = document.getElementById('unsupported-chars-error-msg')
  const panelContent = document.getElementById('unsupported-chars-more-info')
  const infoBtn = document.getElementById('unsupported-chars-btn')

  if (errorMsg && infoBtn && panelContent) {
    ReactDOM.render(<MoreInfo content={panelContent.dataset.content} isJumpstarter={true} />, infoBtn)
  }
}

const removeUnsupportedCharacters = (searchString) => {
  const cleanedString = searchString.replace(ALLOWED_CHARACTERS_REGEX, '')
  hasUnsupportedCharacters = cleanedString.length !== searchString.length

  if (hasUnsupportedCharacters) {
    unsupportedMessage.style.display = 'block'
  } else {
    unsupportedMessage.style.display = 'none'
  }

  return cleanedString
}

const toggleClearButton = (show, hideResults = false) => {
  if (show) {
    clear.style.opacity = 1
    clear.removeAttribute('aria-hidden')
    clear.removeAttribute('tabindex')
  } else if (!resultsPanelOpen || hideResults) {
    clear.style.opacity = 0
    clear.setAttribute('aria-hidden', true)
    clear.setAttribute('tabindex', '-1')
    closeResultsPanel()
  }
}

async function doLookup(query) {
  try {
    // Send explicit GTM event
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: 'domain_lookup', domain: query })

    // Open panel & render loading state
    renderDomains(
      TLDs.map((tld) => ({
        domain: `${query}${tld}`,
        isLoading: true,
      })),
      'Loading...',
      false,
    )
    // Query availability API
    const response = await fetch(`${AVAILABILITY_API_URL}?domains=${query}.nz`)
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`)
    }
    const result = await response.json()
    if (!result[0]?.results?.length) {
      throw new Error('Unexpected response from domain availability API')
    }
    // Filter out domains that are not in the TLDs array, but keep results in order of TLDs array (not response order)
    const domains = TLDs.map((tld) => result[0].results.find((domain) => domain.domainName === `${query}${tld}`))
      .filter((domain) => domain !== undefined)
      .map((domain) => ({
        domain: domain.domainName,
        available: isDomainAvailable(domain),
      }))
    const hasAnyAvailable = domains.some((domain) => domain.available)
    // Update results panel
    renderDomains(
      domains,
      hasAnyAvailable
        ? 'Want to register one or all of these domains?'
        : 'None of these are available, please try another.',
      hasAnyAvailable,
    )
  } catch (error) {
    console.error(error)
    // Display generic error
    renderDomains([], 'Error looking up domain, please try again later.', false)
  }
}

const renderDomains = (domains, message, submitEnabled) => {
  const resultList = form.querySelector('.c-domain-lookup__results')
  // Replace result rows
  resultList.innerHTML = domains
    .map(
      (domain) => `
    <li class="c-domain-lookup__result ${domain.available ? 'c-domain-lookup__result--available' : ''}">
      ${domain.available ? tick.outerHTML : cross.outerHTML}
      <span class="c-domain-lookup__result-domain">${domain.domain}</span>
      <span class="c-domain-lookup__result-status">${domain.available ? 'Available' : 'Not available'}</span>
    </li>
  `,
    )
    .join('')
  // Show message
  form.querySelector('.c-domain-lookup__results-message').textContent = message
  // Format link & enable/disable
  const link = form.querySelector('.c-domain-lookup__link')
  link.href = `${link.getAttribute('data-link')}?d=${input.value}`
  link.classList.toggle('c-btn--disabled', !submitEnabled)
  link.toggleAttribute('aria-disabled', !submitEnabled)
  submitEnabled ? link.removeAttribute('tabindex') : link.setAttribute('tabindex', '-1')
  message.includes('Error') ? (link.style.display = 'none') : (link.style.display = 'flex')
  // Expand the results container (animate height)
  const resultsContainer = form.querySelector('.c-domain-lookup__results-container')
  const resultsInnerHeight = form.querySelector('.c-domain-lookup__results-height')
  // Warning: This height is calculated at the time of render so if the viewport changes, the height will be wrong
  // e.g. when testing different breakpoints and using chrome inspector, you will need to reload each time you change the viewport
  resultsContainer.style.height = `${resultsInnerHeight.clientHeight}px`
  resultsPanelOpen = true
}

const closeResultsPanel = () => {
  const resultsContainer = form.querySelector('.c-domain-lookup__results-container')
  resultsContainer.style.height = '0'
  resultsPanelOpen = false
  if (hasUnsupportedCharacters) {
    unsupportedMessage.style.display = 'none'
    hasUnsupportedCharacters = false
  }
}

const isDomainAvailable = (domain) => {
  return (
    domain.statuses.includes('AVAILABLE') &&
    (!domain.statuses.includes('BLOCKED') || !domain.statuses.includes('BLOCKED_OVERRIDE'))
  )
}
